import type {
  WalletTokenCreateDto,
  WalletTokenDto,
  WalletTokenPatchDto,
  WalletTokenBalanceDto,
} from "@cwm/shared";
import { WalletTokenDetailDto } from "@cwm/shared";

import { apiFetch } from "./api";

/**
 * Thin typed wrapper around the M5 `/wallet-tokens` + `/meta/*` endpoints.
 * Every call goes through `apiFetch`, so auth, JSON parsing, and access-token
 * refresh are handled uniformly.
 */

export interface WalletListQueryParams {
  chainId?: string;
  q?: string;
  page?: number;
  pageSize?: number;
  includeDeleted?: boolean;
  /**
   * Lifecycle filter. `active` (default) mirrors the historical shape;
   * `disabled` drives the "Disabled Wallets" tab; `all` is admin-only mixed
   * visibility. The backend clamps non-admins to `active`.
   */
  status?: "active" | "disabled" | "all";
}

export interface WalletListResponse {
  items: WalletTokenDto[];
  total: number;
  page: number;
  pageSize: number;
}

export interface WalletDetailResponse extends WalletTokenDto {
  walletTokenBalances: WalletTokenBalanceDto[];
}

export interface ChainMeta {
  id: string;
  name: string;
  explorerTxUrlTemplate: string;
  explorerAddressUrlTemplate: string;
  metadata: { theme?: { color?: string } } & Record<string, unknown>;
  active: boolean;
}

/**
 * Wallet freshness cutoff: an `ok` sync (or last balance refresh) older than
 * this reads as amber "Stale" across every surface — the Dashboard summary +
 * per-row status AND the Wallets table. Exported from one module so the two
 * pages can't drift on the threshold (they did: the Wallets table used to
 * render the same 2-month-old wallet green while the Dashboard showed it stale).
 */
export const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length === 0 ? "" : `?${parts.join("&")}`;
}

export function listWalletTokens(
  query: WalletListQueryParams = {},
): Promise<WalletListResponse> {
  return apiFetch<WalletListResponse>(
    `/wallet-tokens${toQuery(query as Record<string, unknown>)}`,
  );
}

export async function getWalletToken(id: string): Promise<WalletDetailResponse> {
  // Zod-parse so a backend contract drift fails loudly in the web client
  // rather than rendering `undefined`s. `WalletTokenDetailDto` matches
  // `WalletDetailResponse` with the exception that `tokensCount` is
  // optional — both types tolerate that.
  const raw = await apiFetch<unknown>(`/wallet-tokens/${encodeURIComponent(id)}`);
  const parsed = WalletTokenDetailDto.safeParse(raw);
  if (!parsed.success) {
    throw new Error(
      `Wallet detail response failed validation: ${parsed.error.message}`,
    );
  }
  return parsed.data as WalletDetailResponse;
}

/**
 * `accountId` is super-admin-only on the wire — the API's
 * `WalletCreateWithAccount` schema accepts it. Tenant users are pinned to
 * their active account via scope so it's safely ignored from them.
 */
export function createWalletToken(
  body: WalletTokenCreateDto & { accountId?: string },
): Promise<WalletTokenDto> {
  return apiFetch<WalletTokenDto>(`/wallet-tokens`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateWalletToken(
  id: string,
  body: WalletTokenPatchDto,
): Promise<WalletTokenDto> {
  return apiFetch<WalletTokenDto>(`/wallet-tokens/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function softDeleteWalletToken(id: string): Promise<void> {
  return apiFetch<void>(`/wallet-tokens/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/**
 * Clear `deletedAt` on a disabled wallet. Admin-only on the server — viewers
 * and managers will receive 403. Returns the refreshed WalletTokenDto so the
 * caller can update its local state without a second round-trip.
 */
export function restoreWalletToken(id: string): Promise<WalletTokenDto> {
  return apiFetch<WalletTokenDto>(
    `/wallet-tokens/${encodeURIComponent(id)}/restore`,
    {
      method: "POST",
    },
  );
}

export function listChains(): Promise<{ items: ChainMeta[] }> {
  return apiFetch<{ items: ChainMeta[] }>(`/meta/chains`);
}

/**
 * Format a server-provided fixed-point USD total into the localised currency
 * string. Returns an em-dash when the total is `null` (no FX coverage yet) so
 * the wallets table never renders a misleading `$0.00`.
 */
export function formatTotalUsd(totalUsd: string | null | undefined): string {
  if (totalUsd == null || totalUsd === "") return "—";
  const asNumber = Number(totalUsd);
  if (!Number.isFinite(asNumber)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(asNumber);
}
